import { useRef, useCallback, useMemo, useEffect } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useToast } from '@/hooks/use-toast';
import { authedFetch } from '@/lib/authed-fetch';
import { dropboxImageUrl } from '@/lib/pulse-draft-attachments';

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

// A separate blot is used for local NEW-pulse previews. It deliberately does
// not change Quill's built-in image sanitizer, so allowing a draft preview in
// one editor cannot make blob: URLs valid in any other editor instance.
const BlockEmbed = Quill.import('blots/block/embed') as any;
class PulseDraftImageBlot extends BlockEmbed {
  static blotName = 'pulse-draft-image';
  static tagName = 'span';
  static className = 'ql-pulse-draft-image';

  static create(value: { src: string; alt?: string }) {
    const node = super.create() as HTMLElement;
    const image = document.createElement('img');
    image.src = value.src;
    image.alt = value.alt || '';
    image.style.maxWidth = '100%';
    image.style.height = 'auto';
    node.dataset.src = value.src;
    node.dataset.alt = value.alt || '';
    node.appendChild(image);
    return node;
  }

  static value(node: HTMLElement) {
    return { src: node.dataset.src || '', alt: node.dataset.alt || '' };
  }
}
Quill.register(PulseDraftImageBlot, true);

export function stripTransientImageUrls(html: string): string {
  if (!html || !html.includes('blob:')) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('img').forEach(image => {
    if (image.getAttribute('src')?.startsWith('blob:')) image.remove();
  });
  container.querySelectorAll('.ql-pulse-draft-image').forEach(node => node.remove());
  return container.innerHTML;
}

function draftImagesForQuill(html: string): string {
  if (!html || !html.includes('blob:')) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll('img').forEach(image => {
    const src = image.getAttribute('src');
    if (!src?.startsWith('blob:')) return;
    const preview = document.createElement('span');
    preview.className = PulseDraftImageBlot.className;
    preview.dataset.src = src;
    preview.dataset.alt = image.getAttribute('alt') || '';
    preview.appendChild(image.cloneNode(true));
    image.replaceWith(preview);
  });
  return container.innerHTML;
}

function draftImagesFromQuill(html: string): string {
  if (!html || !html.includes(PulseDraftImageBlot.className)) return html;
  const container = document.createElement('div');
  container.innerHTML = html;
  container.querySelectorAll<HTMLElement>('.ql-pulse-draft-image').forEach(preview => {
    const image = document.createElement('img');
    image.src = preview.dataset.src || '';
    image.alt = preview.dataset.alt || '';
    image.style.maxWidth = '100%';
    image.style.height = 'auto';
    preview.replaceWith(image);
  });
  return container.innerHTML;
}

async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('image', file);

  const response = await authedFetch('/api/uploads/images', {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to upload image');
  }

  const data = await response.json();
  return data.url;
}

export async function uploadPulseImage(
  pulseId: number,
  file: File,
  fetcher: typeof authedFetch = authedFetch,
): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetcher(`/api/pulses/${pulseId}/attachments`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Image upload failed (HTTP ${response.status})`);
  }
  if (!data.sharedLink) {
    throw new Error('Image was attached, but no durable image link was returned.');
  }
  return dropboxImageUrl(data.sharedLink);
}

function validateImage(file: File): string | null {
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
  
  if (!allowedTypes.includes(file.type)) {
    return `Invalid file type: ${file.type}. Only JPEG, PNG, GIF, WebP, and SVG are allowed.`;
  }
  
  if (file.size > MAX_IMAGE_SIZE) {
    return `File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Maximum size is 10MB.`;
  }
  
  return null;
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  readOnly?: boolean;
  showImageButton?: boolean;
  compact?: boolean;
  /** Only for the editor used while creating a pulse that has no id yet. */
  allowTemporaryBlobImages?: boolean;
  /** Existing pulse images are stored through its attachment endpoint. */
  pulseId?: number | null;
  imageUploader?: (file: File) => Promise<string>;
  onImageUploadPendingChange?: (pending: boolean) => void;
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  className = '',
  style,
  readOnly = false,
  showImageButton = true,
  compact = false,
  allowTemporaryBlobImages = false,
  pulseId,
  imageUploader,
  onImageUploadPendingChange,
}: RichTextEditorProps) {
  const quillRef = useRef<ReactQuill>(null);
  const pendingUploadsRef = useRef(0);
  const mountedRef = useRef(true);
  const pendingChangeRef = useRef(onImageUploadPendingChange);
  const { toast } = useToast();

  useEffect(() => {
    pendingChangeRef.current = onImageUploadPendingChange;
  }, [onImageUploadPendingChange]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pendingUploadsRef.current = 0;
      pendingChangeRef.current?.(false);
    };
  }, []);

  const handleImageUpload = useCallback(async (file: File) => {
    const error = validateImage(file);
    if (error) {
      toast({ title: 'Image Upload Failed', description: error, variant: 'destructive' });
      return;
    }

    pendingUploadsRef.current += 1;
    if (pendingUploadsRef.current === 1) pendingChangeRef.current?.(true);
    try {
      toast({ title: 'Uploading image...', description: file.name });
      const url = await (imageUploader
        ? imageUploader(file)
        : pulseId != null
          ? uploadPulseImage(pulseId, file)
          : uploadImage(file));
      
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection(true);
        quill.insertEmbed(
          range.index,
          allowTemporaryBlobImages ? 'pulse-draft-image' : 'image',
          allowTemporaryBlobImages ? { src: url, alt: file.name } : url,
        );
        quill.setSelection(range.index + 1, 0);
      }
      
      if (mountedRef.current) toast({ title: 'Image uploaded successfully' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (mountedRef.current) {
        toast({ title: 'Image Upload Failed', description: message, variant: 'destructive' });
      }
    } finally {
      pendingUploadsRef.current = Math.max(0, pendingUploadsRef.current - 1);
      if (mountedRef.current && pendingUploadsRef.current === 0) {
        pendingChangeRef.current?.(false);
      }
    }
  }, [toast, imageUploader, pulseId, allowTemporaryBlobImages, onImageUploadPendingChange]);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml');
    input.click();
    
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        await handleImageUpload(file);
      }
    };
  }, [handleImageUpload]);

  useEffect(() => {
    if (!quillRef.current) return;
    
    const quill = quillRef.current.getEditor();
    const toolbar = quill.getModule('toolbar') as { addHandler?: (name: string, handler: () => void) => void } | null;
    
    if (toolbar?.addHandler && showImageButton) {
      toolbar.addHandler('image', imageHandler);
    }

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      
      const itemsArray = Array.from(items);
      for (let i = 0; i < itemsArray.length; i++) {
        const item = itemsArray[i];
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          e.stopPropagation();
          
          const file = item.getAsFile();
          if (file) {
            handleImageUpload(file);
          }
          return;
        }
      }
    };

    const handleDrop = (e: DragEvent) => {
      const files = e.dataTransfer?.files;
      if (!files) return;
      
      const filesArray = Array.from(files);
      for (let i = 0; i < filesArray.length; i++) {
        const file = filesArray[i];
        if (file.type.startsWith('image/')) {
          e.preventDefault();
          e.stopPropagation();
          handleImageUpload(file);
          return;
        }
      }
    };

    const editorElement = quill.root;
    editorElement.addEventListener('paste', handlePaste as EventListener);
    editorElement.addEventListener('drop', handleDrop as EventListener);

    return () => {
      editorElement.removeEventListener('paste', handlePaste as EventListener);
      editorElement.removeEventListener('drop', handleDrop as EventListener);
    };
  }, [imageHandler, handleImageUpload, showImageButton]);

  const modules = useMemo(() => {
    if (compact) {
      return {
        toolbar: false,
        clipboard: { matchVisual: false }
      };
    }

    const toolbarOptions = showImageButton
      ? [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          ['link', 'image'],
          ['clean']
        ]
      : [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'indent': '-1' }, { 'indent': '+1' }],
          ['link'],
          ['clean']
        ];

    return {
      toolbar: toolbarOptions,
      clipboard: { matchVisual: false }
    };
  }, [compact, showImageButton]);

  const formats = useMemo(() => [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'link', 'image', ...(allowTemporaryBlobImages ? ['pulse-draft-image'] : [])
  ], [allowTemporaryBlobImages]);

  const editorValue = useMemo(
    () => allowTemporaryBlobImages ? draftImagesForQuill(value) : stripTransientImageUrls(value),
    [allowTemporaryBlobImages, value],
  );
  const handleChange = useCallback((html: string) => {
    onChange(allowTemporaryBlobImages
      ? draftImagesFromQuill(html)
      : stripTransientImageUrls(html));
  }, [allowTemporaryBlobImages, onChange]);

  return (
    <ReactQuill
      useSemanticHTML={false}
      ref={quillRef}
      theme="snow"
      value={editorValue}
      onChange={handleChange}
      placeholder={placeholder}
      readOnly={readOnly}
      modules={modules}
      formats={formats}
      className={`rich-text-dark ${className}`}
      style={style}
    />
  );
}

export default RichTextEditor;
